import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'
import { useIdempotencyKey } from '@/lib/use-idempotency-key'

/**
 * Catálogo da Westhetics: a lista mestre de procedimentos e a base procedimento × contrato
 * (docs/api/admin.md, "Catálogo da Westhetics"; RN-ADM-70 a 75).
 */

export interface PersonRef {
  id: string | null
  name: string
}

export type TemplateState = 'draft' | 'published' | 'archived'

export interface CatalogProcedureTemplate {
  id: string
  name: string
  category: string
  isRequired: boolean
  state: TemplateState
  currentVersionLabel: string | null
}

export interface CatalogProcedure {
  id: string
  name: string
  groupName: string
  description: string | null
  aliases: string[]
  templates: CatalogProcedureTemplate[]
  hasConsentForm: boolean
  usage: { clinicCount: number; procedureCount: number }
  archivedAt: string | null
  archivedBy: PersonRef | null
  createdAt: string
  updatedAt: string
  updatedBy: PersonRef | null
}

/** Item da lista de escolha da clínica (`GET /admin/catalog/procedures/options`). */
export interface CatalogProcedureOption {
  id: string
  name: string
  groupName: string
  description: string | null
  aliases: string[]
  documentCount: number
  hasConsentForm: boolean
  isInClinicCatalog: boolean
}

export type CatalogFilter = 'pending' | 'active' | 'archived'

export interface CatalogListMeta extends ListMeta {
  counts: Record<CatalogFilter, number>
  groups: string[]
  openRequestCount: number
}

export interface CatalogProcedureInput {
  name: string
  groupName: string
  description: string | null
  aliases: string[]
  templates?: { templateId: string; isRequired: boolean }[]
}

export interface CatalogRequest {
  id: string
  name: string
  note: string | null
  status: 'open' | 'added' | 'declined'
  clinic: { id: string; name: string }
  catalogProcedure: { id: string; name: string } | null
  resolutionNote: string | null
  createdAt: string
  createdBy: PersonRef
  resolvedAt: string | null
  resolvedBy: PersonRef | null
}

/**
 * Documento que pode acompanhar um procedimento (`GET /documents/templates`, docs/api/documentos.md):
 * só o que a busca de "Vincular documento" mostra.
 */
export interface TemplateOption {
  id: string
  name: string
  category: string
  state: TemplateState
  currentVersion: { label: string } | null
}

export const catalogKeys = {
  all: ['catalog-procedures'] as const,
  list: (params: object) => ['catalog-procedures', 'list', params] as const,
  options: ['catalog-procedures', 'options'] as const,
  detail: (id: string) => ['catalog-procedures', 'detail', id] as const,
  requests: (filter: string) => ['catalog-procedures', 'requests', filter] as const,
  templateOptions: (search: string) => ['catalog-procedures', 'template-options', search] as const,
}

export function useCatalogProcedures(params: {
  filter: CatalogFilter
  search?: string
  group?: string
  page?: number
  perPage?: number
}) {
  const api = useApi()
  return useQuery({
    queryKey: catalogKeys.list(params),
    queryFn: () =>
      api.list<CatalogProcedure, CatalogListMeta>('/admin/catalog/procedures', {
        filter: params.filter,
        search: params.search,
        group: params.group,
        page: params.page ?? 1,
        perPage: params.perPage ?? 25,
      }),
    placeholderData: (previous) => previous,
  })
}

/** A lista mestre inteira, sem os arquivados, para escolher (RN-ADM-72). */
export function useCatalogOptions(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: catalogKeys.options,
    queryFn: () => api.get<CatalogProcedureOption[]>('/admin/catalog/procedures/options'),
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  })
}

export function useCatalogProcedure(id: string | undefined) {
  const api = useApi()
  return useQuery({
    queryKey: catalogKeys.detail(id ?? ''),
    queryFn: () => api.get<CatalogProcedure>(`/admin/catalog/procedures/${id}`),
    enabled: Boolean(id),
  })
}

export function useSaveCatalogProcedure(id?: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  const idempotency = useIdempotencyKey()
  return useMutation({
    mutationFn: (input: CatalogProcedureInput) =>
      id
        ? api.put<CatalogProcedure>(`/admin/catalog/procedures/${id}`, input)
        : api.post<CatalogProcedure>('/admin/catalog/procedures', input, {
            idempotencyKey: idempotency.keyFor(input),
          }),
    onSuccess: () => {
      idempotency.reset()
      return queryClient.invalidateQueries({ queryKey: catalogKeys.all })
    },
  })
}

export function useArchiveCatalogProcedure() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'archive' | 'restore' }) =>
      api.post<CatalogProcedure>(`/admin/catalog/procedures/${id}/${action}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.all }),
  })
}

/**
 * A busca de documentos a vincular (RN-ADM-73): termo de consentimento ou termo avulso do público
 * Pacientes. Vem com `filter=all` porque o documento pode ainda estar em rascunho; o arquivado
 * chega junto e a tela o deixa de fora.
 */
export function useTemplateOptions(search: string, options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: catalogKeys.templateOptions(search),
    queryFn: () =>
      api.list<TemplateOption>('/documents/templates', {
        audience: 'patient',
        category: ['informed_consent', 'standalone_form'],
        filter: 'all',
        search,
        perPage: 25,
      }),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  })
}

export function useCatalogRequests(
  filter: 'open' | 'resolved',
  options: { enabled?: boolean } = {},
) {
  const api = useApi()
  return useQuery({
    queryKey: catalogKeys.requests(filter),
    queryFn: () =>
      api.list<CatalogRequest, ListMeta & { counts: { open: number; resolved: number } }>(
        '/admin/catalog/procedure-requests',
        { filter, perPage: 50 },
      ),
    enabled: options.enabled ?? true,
  })
}

export type ResolveRequestInput =
  { outcome: 'added'; catalogProcedureId: string } | { outcome: 'declined'; resolutionNote: string }

export function useResolveCatalogRequest() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & ResolveRequestInput) =>
      api.post<CatalogRequest>(`/admin/catalog/procedure-requests/${id}/resolve`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogKeys.all }),
  })
}
